/**
 * The do-not list, drawn so it cannot be scrolled past.
 *
 * `data/clients.md` records that the single most common cause of a complaint in this work is
 * touching something the owner did not want touched. That is why the schema gives it a column of
 * its own instead of burying it in notes, and it is why it gets a panel of its own here instead of
 * a line in a list. Coral is used sparingly in this interface, and this is one of the two places
 * it earns its keep.
 *
 * When it is empty it still renders, because "nobody has asked yet" is a fact worth seeing. An
 * absent panel reads as an empty list, and an empty list reads as permission.
 */
export function DoNotPanel({ text }: { text: string | null }) {
  if (!text) {
    return (
      <div className="mt-3 rounded-lg border border-dashed border-navy/30 px-3 py-2">
        <p className="font-serif text-sm tracking-wide uppercase">Do not touch</p>
        <p className="mt-1 text-sm text-navy/70">
          Nothing written down yet. Ask on the first visit and put it here. This is the most common
          cause of a complaint in this work, and it costs one line to prevent.
        </p>
      </div>
    );
  }
  return (
    <div className="mt-3 rounded-lg border border-l-4 border-coral/40 border-l-coral bg-coral/8 px-3 py-2">
      <p className="font-serif text-sm tracking-wide uppercase">Do not touch</p>
      <p className="mt-1 text-sm whitespace-pre-line">{text}</p>
    </div>
  );
}

/**
 * WHAT THEY CHECK FIRST (ops #86), right under the do-not list: the three things the household said
 * they look at first when they walk in, as the home said them when this sheet was frozen. Nothing at
 * all when none were said, unlike the do-not list: an empty list here gives no permission, and a
 * cleaner who reads "nothing" learns nothing she can act on at the door.
 */
export function ChecksFirstPanel({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <div className="mt-2 rounded-lg border border-l-4 border-gold/50 border-l-gold bg-parchment px-3 py-2">
      <p className="font-serif text-sm tracking-wide uppercase">They check first</p>
      <ol className="mt-1 list-decimal pl-5 text-sm">
        {text.split('\n').map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ol>
    </div>
  );
}
